import {
  useEffect,
  useLayoutEffect,
  useRef,
  type ClipboardEvent as ReactClipboardEvent,
  type CSSProperties,
  type KeyboardEvent as ReactKeyboardEvent,
  type MutableRefObject,
} from 'react';
import {
  textElementStyle,
  type Paragraph,
  type TextElement,
  type TrainingDesign,
} from '@allemnii-pulse/shared';
import {
  buildEditableHtml,
  domToPos,
  parseEditable,
  readDomSelection,
  setDomSelection,
} from './editableDom.js';
import {
  clampPos,
  deleteRange,
  fullRange,
  insertText,
  isCollapsed,
  mergeWithPrevious,
  orderedRange,
  paragraphText,
  splitParagraph,
  type TextPos,
  type TextRange,
} from './richText.js';

export type FormatShortcut = 'bold' | 'italic' | 'underline';

/**
 * A text element being edited in place. Typing inside one paragraph is left to the browser and
 * read back from the DOM on each input event (coalesced into one undo step per burst). Anything
 * that changes paragraph structure — Enter, deleting across or between paragraphs, paste — is
 * intercepted and applied to the model instead, then the DOM is rebuilt from it, so the DOM the
 * browser edits always stays in the shape parseEditable expects.
 */
export function EditableText({
  element,
  design,
  selectionRef,
  initialPoint,
  onParagraphs,
  onSelection,
  onExit,
  onUndo,
  onRedo,
  onFormatShortcut,
}: {
  element: TextElement;
  design: TrainingDesign;
  /** The latest model selection — written here, read by the toolbar and to restore the caret
   * after the model changes from outside (toolbar formatting, undo). */
  selectionRef: MutableRefObject<TextRange | null>;
  /** Where the double-click that started editing happened, to put the caret there. */
  initialPoint: { x: number; y: number } | null;
  onParagraphs: (paragraphs: Paragraph[], coalesce: boolean) => void;
  onSelection: (range: TextRange | null) => void;
  onExit: () => void;
  onUndo: () => void;
  onRedo: () => void;
  onFormatShortcut: (kind: FormatShortcut) => void;
}) {
  const rootRef = useRef<HTMLDivElement>(null);
  /** The paragraphs the DOM currently shows. A prop change to anything else came from outside. */
  const synced = useRef<Paragraph[] | null>(null);

  function rebuild(paragraphs: Paragraph[], selection: TextRange | null) {
    const root = rootRef.current;
    if (!root) return;
    synced.current = paragraphs;
    // buildEditableHtml escapes every text and attribute value, and the only styles it emits come
    // from model fields the schema constrains (hex colors, the fixed font list, numbers).
    root.innerHTML = buildEditableHtml(paragraphs, design);
    if (document.activeElement !== root) root.focus({ preventScroll: true });
    const range = selection ?? { start: fullRange(paragraphs).end, end: fullRange(paragraphs).end };
    const start = clampPos(paragraphs, range.start);
    const end = clampPos(paragraphs, range.end);
    setDomSelection(root, start, end);
    selectionRef.current = { start, end };
    onSelection({ start, end });
  }

  /** Applies a structural edit to the model and shows it, with the caret at `caret`. */
  function applyStructural(next: Paragraph[], caret: TextPos) {
    onParagraphs(next, false);
    rebuild(next, { start: caret, end: caret });
  }

  // First render: build the DOM and place the caret where the user double-clicked.
  useLayoutEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    rebuild(element.paragraphs, null);
    if (initialPoint) {
      const doc = document as Document & {
        caretPositionFromPoint?: (
          x: number,
          y: number,
        ) => { offsetNode: Node; offset: number } | null;
      };
      let point: { node: Node; offset: number } | null = null;
      if (doc.caretRangeFromPoint) {
        const r = doc.caretRangeFromPoint(initialPoint.x, initialPoint.y);
        if (r) point = { node: r.startContainer, offset: r.startOffset };
      } else if (doc.caretPositionFromPoint) {
        const p = doc.caretPositionFromPoint(initialPoint.x, initialPoint.y);
        if (p) point = { node: p.offsetNode, offset: p.offset };
      }
      const pos = point && domToPos(root, point.node, point.offset);
      if (pos) {
        setDomSelection(root, pos);
        selectionRef.current = { start: pos, end: pos };
        onSelection({ start: pos, end: pos });
      }
    }
    // Only on mount — later model changes are handled by the effect below.
  }, []);

  // A model change that didn't come from this DOM (toolbar formatting, undo/redo): re-render it,
  // keeping the selection.
  useLayoutEffect(() => {
    if (synced.current === null || element.paragraphs === synced.current) return;
    rebuild(element.paragraphs, selectionRef.current);
  }, [element.paragraphs]);

  useEffect(() => {
    function handleSelectionChange() {
      const root = rootRef.current;
      if (!root) return;
      const sel = readDomSelection(root);
      if (!sel) return;
      const range = orderedRange(sel.start, sel.end);
      selectionRef.current = range;
      onSelection(range);
    }
    document.addEventListener('selectionchange', handleSelectionChange);
    return () => document.removeEventListener('selectionchange', handleSelectionChange);
  }, [onSelection, selectionRef]);

  function currentRange(): TextRange | null {
    const root = rootRef.current;
    if (!root) return null;
    const sel = readDomSelection(root);
    return sel ? orderedRange(sel.start, sel.end) : null;
  }

  function handleInput() {
    const root = rootRef.current;
    if (!root) return;
    const parsed = parseEditable(root);
    // The browser occasionally leaves content outside a paragraph div (e.g. after replacing a
    // whole selection); put the DOM back into shape before carrying on.
    const malformed = Array.from(root.childNodes).some(
      (n) => !(n instanceof HTMLElement) || n.dataset.para !== '1',
    );
    onParagraphs(parsed, true);
    if (malformed) rebuild(parsed, null);
    else synced.current = parsed;
  }

  function handleBeforeInput(e: InputEvent) {
    const root = rootRef.current;
    const range = currentRange();
    if (!root || !range) return;
    const paragraphs = parseEditable(root);
    const spansParagraphs = range.start.p !== range.end.p;
    switch (e.inputType) {
      case 'insertParagraph':
      case 'insertLineBreak': {
        e.preventDefault();
        const next = splitParagraph(deleteRange(paragraphs, range), range.start);
        applyStructural(next, { p: range.start.p + 1, o: 0 });
        return;
      }
      case 'insertText':
      case 'insertReplacementText': {
        if (!spansParagraphs) return;
        e.preventDefault();
        const text = e.data ?? e.dataTransfer?.getData('text/plain') ?? '';
        const result = insertText(deleteRange(paragraphs, range), range.start, text);
        applyStructural(result.paragraphs, result.caret);
        return;
      }
      case 'deleteContentBackward':
      case 'deleteContentForward':
      case 'deleteWordBackward':
      case 'deleteWordForward':
      case 'deleteSoftLineBackward':
      case 'deleteSoftLineForward':
      case 'deleteHardLineBackward':
      case 'deleteHardLineForward':
      case 'deleteByCut': {
        if (!isCollapsed(range)) {
          if (!spansParagraphs) return;
          e.preventDefault();
          applyStructural(deleteRange(paragraphs, range), range.start);
          return;
        }
        const { p, o } = range.start;
        const backward = e.inputType.includes('Backward');
        if (backward && o === 0) {
          e.preventDefault();
          if (p === 0) {
            // Backspace at the very start of a bulleted first line removes the bullet.
            if (paragraphs[0]?.bullet)
              applyStructural(
                [{ ...paragraphs[0], bullet: undefined }, ...paragraphs.slice(1)],
                range.start,
              );
            return;
          }
          const prevLen = paragraphText(paragraphs[p - 1]!).length;
          applyStructural(mergeWithPrevious(paragraphs, p), { p: p - 1, o: prevLen });
          return;
        }
        if (!backward && o === paragraphText(paragraphs[p]!).length && p < paragraphs.length - 1) {
          e.preventDefault();
          applyStructural(mergeWithPrevious(paragraphs, p + 1), range.start);
        }
        return;
      }
      case 'insertFromDrop':
      case 'deleteByDrag':
        e.preventDefault();
        return;
      case 'formatBold':
      case 'formatItalic':
      case 'formatUnderline':
        e.preventDefault();
        onFormatShortcut(
          e.inputType === 'formatBold'
            ? 'bold'
            : e.inputType === 'formatItalic'
              ? 'italic'
              : 'underline',
        );
        return;
      case 'historyUndo':
      case 'historyRedo':
        e.preventDefault();
        return;
      default:
        if (e.inputType.startsWith('format')) e.preventDefault();
    }
  }

  // React's onBeforeInput is a synthetic polyfill without inputType; listen natively.
  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    const handler = (e: Event) => handleBeforeInput(e as InputEvent);
    root.addEventListener('beforeinput', handler);
    return () => root.removeEventListener('beforeinput', handler);
  });

  function handlePaste(e: ReactClipboardEvent) {
    e.preventDefault();
    const root = rootRef.current;
    const range = currentRange();
    if (!root || !range) return;
    const text = e.clipboardData.getData('text/plain');
    if (!text) return;
    const result = insertText(deleteRange(parseEditable(root), range), range.start, text);
    applyStructural(result.paragraphs, result.caret);
  }

  function handleKeyDown(e: ReactKeyboardEvent) {
    const mod = e.metaKey || e.ctrlKey;
    if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      onExit();
      return;
    }
    if (!mod) {
      e.stopPropagation();
      return;
    }
    const key = e.key.toLowerCase();
    if (key === 'z' || key === 'y') {
      e.preventDefault();
      e.stopPropagation();
      if (key === 'y' || e.shiftKey) onRedo();
      else onUndo();
      return;
    }
    if (key === 'b' || key === 'i' || key === 'u') {
      e.preventDefault();
      e.stopPropagation();
      onFormatShortcut(key === 'b' ? 'bold' : key === 'i' ? 'italic' : 'underline');
      return;
    }
    e.stopPropagation();
  }

  const style: CSSProperties = {
    // The wrapper on the canvas places and rotates the box; drawn here at the origin, upright.
    ...(textElementStyle({ ...element, x: 0, y: 0, rotation: 0 }, design) as CSSProperties),
    overflow: 'visible',
    outline: 'none',
    whiteSpace: 'pre-wrap',
    cursor: 'text',
  };

  return (
    <div
      ref={rootRef}
      contentEditable
      suppressContentEditableWarning
      spellCheck
      style={style}
      onInput={handleInput}
      onPaste={handlePaste}
      onDrop={(e) => e.preventDefault()}
      onKeyDown={handleKeyDown}
      onPointerDown={(e) => e.stopPropagation()}
    />
  );
}
